import { ApiService } from '@b2b/common/services/api/types';
import { CalendarService } from '@b2b/common/services/calendar';
import { PermissionsService } from '@b2b/common/services/permissions';
import { UserPreferencesService } from '@b2b/common/services/userPreferences/types';
import { TODO } from '@b2b/types/core';
import { StateService } from '@uirouter/angularjs';
import angular, { IScope } from 'angular';
import { CalendarEventDirectiveModule } from './createEvent/createEvent';

type FullCalendar = TODO;

CalendarDirective.$inject = ['CalendarService', 'PermissionsService', 'UserPreferencesService', 'ApiService'];

function CalendarDirective(
  CalendarService: CalendarService,
  PermissionsService: PermissionsService,
  UserPreferencesService: UserPreferencesService,
  ApiService: ApiService,
) {
  let renderTimer: number;

  return {
    restrict: 'A',
    replace: false,
    template: '<div data-calendar-create-event calendarCreateEventId="{{ id }}"></div>',
    scope: {
      id: '@',
      events: '=',
    },

    link: function (
      $scope: IScope & { id: string; events: TODO; calendar: TODO },
      $element: JQuery & { fullCalendar: FullCalendar },
    ) {
      console.log('calendar::link -> "' + $scope.id + '"');
      console.log('calendar -> events: ', $scope.events);

      const hourFormat = UserPreferencesService.getDateFormat('standardTime', 'hourFormat');

      const initialDate = CalendarService.current.hasOwnProperty('startTimeTracker')
        ? CalendarService.current.startTimeTracker
        : new Date();

      // Used for date sharing with scheduler
      const defaultView = CalendarService.current.hasOwnProperty('defaultView')
        ? CalendarService.current.defaultView
        : 'month';

      $scope.$on('UserPreferencesService:onDateFormatLoad', onDateFormatLoad);

      loadCalendar();

      window.clearTimeout(renderTimer);
      renderTimer = window.setTimeout(() => {
        $element.fullCalendar('gotoDate', initialDate.getFullYear(), initialDate.getMonth(), initialDate.getDate());
        $element.fullCalendar('render');
      });

      function loadCalendar() {
        $element.fullCalendar('destroy');
        $element.fullCalendar({
          editable: false,
          weekMode: 'fixed',
          capWeekHeight: false,
          capDayHeight: false,
          axisFormat: getTimeFormatByKey(hourFormat),
          firstDay: UserPreferencesService.getStartDayOfWeek(),
          header: {
            left: 'today',
            center: ['prev', 'title', 'next'].join(' '),
            right: ['month', 'agendaWeek', 'agendaDay'].join(','),
          },
          titleFormat: {
            month: checkDateFormat(UserPreferencesService.getDateFormat('month', 'dateFormat')),
            week: checkDateFormat(UserPreferencesService.getDateFormat('week', 'dateFormat')),
            day: checkDateFormat(UserPreferencesService.getDateFormat('day', 'dateFormat')),
          },
          viewRender: $scope.calendar.onViewRender,
          eventClick: $scope.calendar.onEventClick,
          dayClick: $scope.calendar.onDayClick,
          events: $scope.events,
          timeFormat: getTimeFormatByKey(hourFormat),
          defaultView,
        });
      }
      function onDateFormatLoad(e, data) {
        loadCalendar();
        $element.fullCalendar('render');
      }

      function getTimeFormatByKey(hourFormat: string) {
        const hash = {
          'HH:mm': 'HH:mm',
          default: 'h:mm tt',
        };
        return hash[hourFormat] || hash['default'];
      }

      function checkDateFormat(date) {
        return date.indexOf('EEEE') !== -1 ? date.replace(/EEEE/, 'dddd') : date;
      }
    },

    controller: [
      '$scope',
      '$state',
      '$element',
      'CalendarService',
      function CalendarController(
        $scope: IScope & { id: string; events: TODO; calendar: TODO },
        $state: StateService,
        $element: JQuery & { fullCalendar: FullCalendar },
        CalendarService: CalendarService,
      ) {
        const ctrl = {
          model: {
            events: [],
          },

          init: function () {
            $scope.$on('calendarCreateEvent::edit', ctrl.onOnCalendarCreateEdit);
            $scope.$on('calendarCreateEvent::view', ctrl.onOnCalendarCreateView);
            $scope.$on('calendarCreateEvent::cancel', ctrl.onOnCalendarCreateCancel);
            $scope.$on('calendarCreateEvent::complete', ctrl.onOnCalendarCreateComplete);
            $scope.$on('calendarCreateEvent::events', ctrl.onOnCalendarCreateEvents);
            $scope.$on('calendarCreateEvent::update', ctrl.onOnCalendarCreateUpdate);
            $scope.$on('calendarCreateEvent::todayClick', ctrl.onOnCalendarCreateTodayClick);
            $scope.$on('$destroy', () => $element.fullCalendar('destroy'));
          },

          // Prune temporary events
          prune: function () {
            if (Array.isArray($scope.events)) {
              for (let i = 0, ii = $scope.events.length; i < ii; i++) {
                if ($scope.events[i].color === 'lightblue') {
                  $scope.events.splice(i, 1);
                }
              }
            }
          },

          onEventClick: function (calEvent, jsEvent, view) {
            ApiService.event.detail(calEvent.id).then((response) => {
              calEvent.requestSummary = { ...response.data.payload.eventResourceSummary };

              $scope.$emit('calendar[' + $scope.id + ']::onEventClick', {
                calEvent: calEvent,
                event: jsEvent,
                view: view,
              });

              $scope.$broadcast('calendar::onEventClick', {
                calEvent: calEvent,
                event: jsEvent,
                view: view,
              });
            });
          },

          onOnCalendarCreateTodayClick: function (e, data) {
            $scope.$emit('calendar[' + $scope.id + ']::onTodayClick');
          },

          onOnCalendarCreateEdit: function (e, data) {
            $state.go('application.templates.edit.description', { id: data.model.id });
          },

          onOnCalendarCreateView: function (e, data) {
            $state.go('application.programsInstance', { id: data.model.id });
          },

          onOnCalendarCreateComplete: function (e, data) {
            CalendarService.create(data);
            $state.go('application.templates.create.description');
          },

          onOnCalendarCreateEvents: function (e, data) {
            $element.fullCalendar('refetchEvents');
            console.log('onOnCalendarCreateEvents', $scope.events);
          },

          onOnCalendarCreateCancel: function () {
            ctrl.prune();
            $element.fullCalendar('refetchEvents');
          },

          onOnCalendarCreateUpdate: function (e, data) {
            $element.fullCalendar('gotoDate', data.date.getFullYear(), data.date.getMonth(), data.date.getDate());
            $element.fullCalendar('render');
          },

          onDayClick: function (date, jsEvent, view) {
            if (PermissionsService.check('EVENT_TEMPLATE_CREATE')) {
              ctrl.prune();

              date.setHours(12);

              $scope.events.push({
                color: 'lightblue',
                title: '',
                start: date,
                allDay: false,
              });

              $scope.$emit('calendar[' + $scope.id + ']::onDayClick', {
                date: date,
                event: jsEvent,
                view: view,
              });

              $scope.$broadcast('calendar::onDayClick', {
                date: date,
                event: jsEvent,
                view: view,
              });

              $element.fullCalendar('refetchEvents');
            }
          },

          onViewRender: function (view, element) {
            $scope.$emit('calendar[' + $scope.id + ']::onViewRender', {
              view: view,
              element: element,
            });

            $scope.$broadcast('calendar::onViewRender', {
              view: view,
              element: element,
            });
          },
        };

        $scope.calendar = ctrl;
        ctrl.init();
      },
    ],
  };
}

export const CalendarDirectiveModule = angular
  .module('acme.common.directive.calendar', [
    'ui.bootstrap',
    'acme.common.service.api',
    'acme.common.service.calendar',
    'acme.common.service.permissions',
    CalendarEventDirectiveModule,
    'acme.common.service.userPreferences',
  ])
  .directive('calendar', CalendarDirective).name;
